import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { CalendarDate, parseDate } from "@internationalized/date";
import moment from "moment";
import { Plus } from "lucide-react";
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import AppButton from "../../common_components/AppButton";
import { ExportButton, Dropdown } from "../../common_components/FormInputs";
import CustomDatePicker from "../../common_components/daterangepicker/CustomDatePicker";
import IOSSwitch from "../../common_components/IOSSwitch";
import CommonTable from "../../common_components/CommonTable";
import CommonLoader from "../../common_components/CommonLoader";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { SUMMARY_ROW_POSITIONS, TABLE_TYPES, EXPAND_TRIGGERS } from "../../common_components/tableConstants";
import { VarianceBadge } from "../../common_components/VarianceBadge";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import AddArticleModal from "./components/AddArticleModal";
import AddPlacementDrawer from "../PublisherReporting/components/AddPlacementDrawer";
import PublishedArticlesModal from "./components/PublishedArticlesModal";
import exportOutreachPerformance from "./exportOutreachPerformance";
import { selectCanManagePlacements, selectCanManagePublisherReportingPlacements, selectCanRefreshOutreach } from "../../redux/authSlice";
import { fetchAllClientList, fetchDesignationWiseUsers, selectAllClientList, selectDesignationWiseUsers, } from "../../redux/clientDataSlice";
import { fetchOutreachPerformance, syncOutreachLeads, setGroupBy, setDateRange, setCompareDateRange, selectOutreachPerformanceEntities, selectOutreachPerformanceTeamTotal, selectOutreachPerformanceBySpecialist, selectOutreachPerformanceSpecialists, selectOutreachPerformanceLastUpdated, } from "../../redux/outreachPerformanceSlice";
const toCalendarDate = (iso) => {
    const m = moment(iso);
    return new CalendarDate(m.year(), m.month() + 1, m.date());
};
const pct = (value) => `${(value ?? 0).toFixed(1)}%`;
const SENTIMENT_KEYS = ["positive", "neutral", "negative"];
// per_goal (as returned by the API) is a signed variance — (actual-goal)/goal*100.
// The "% Goal" badge needs a straight goal-attainment percentage instead
// (actual/goal*100), so it's derived here rather than trusting that field.
const attainmentPct = (actual, goal) => goal ? ((actual ?? 0) / goal) * 100 : 0;
const SPECIALIST_COLUMNS = [
    { id: "rowLabel", label: "Outreach Specialist", width: "200px", align: "left", sortable: true },
    { id: "trackersCount", label: "# of Trackers", width: "125px", align: "center", sortable: true },
    {
        id: "enrollmentsGroup", label: "# of Enrollments", align: "center", children: [
            { id: "outreachActual", label: "Enrolled", width: "125px", align: "center", sortable: true },
            { id: "enrollmentsReach", label: "Reach %", width: "95px", align: "center", sortable: true },
        ],
    },
    { id: "leadsActual", label: "Leads", width: "125px", align: "center", sortable: true },
    {
        id: "placementsGroup", label: "Placements", align: "center", children: [
            { id: "placementsGoal", label: "Goal", width: "95px", align: "center", sortable: true },
            { id: "placementsPlaced", label: "Placed", width: "125px", align: "center", sortable: true },
            { id: "placementsVar", label: "VAR", width: "90px", align: "center", sortable: true },
        ],
    },
    {
        id: "conversionGroup", label: "Conversion", align: "center", children: [
            { id: "conversionOutLead", label: "Out→Lead", width: "100px", align: "center", sortable: true },
            {
                id: "conversionLeadPlace",
                label: (_jsxs(Box, { component: "span", className: "all-Font-Sizes", sx: {
                        display: "inline-block",
                        textAlign: "center",
                        fontSize: "12px !important",
                        fontFamily: "Inter",
                        fontWeight: 600,
                        lineHeight: 1.25,
                        color: "inherit",
                    }, children: ["Lead\u2192Placement", _jsx("br", {}), "Conv. Rate"] })),
                width: "140px",
                align: "center",
                sortable: true,
            },
        ],
    },
];
const CLIENT_METRIC_COLUMNS = [
    { id: "trackersCount", label: "# of Trackers", width: "125px", align: "center", sortable: true },
    {
        id: "enrollmentsGroup", label: "# of Enrollments", align: "center", children: [
            { id: "outreachActual", label: "Enrolled", width: "125px", align: "center", sortable: true },
            { id: "enrollmentsReach", label: "Reach %", width: "95px", align: "center", sortable: true },
        ],
    },
    { id: "leadsActual", label: "Leads", width: "125px", align: "center", sortable: true },
    {
        id: "placementsGroup", label: "Placements", align: "center", children: [
            { id: "placementsGoal", label: "Goal", width: "95px", align: "center", sortable: true },
            { id: "placementsPlaced", label: "Placed", width: "125px", align: "center", sortable: true },
            { id: "placementsVar", label: "VAR", width: "90px", align: "center", sortable: true },
        ],
    },
    {
        id: "conversionGroup", label: "Conversion", align: "center", children: [
            { id: "conversionOutLead", label: "Out→Lead", width: "100px", align: "center", sortable: true },
            {
                id: "conversionLeadPlace",
                label: (_jsxs(Box, { component: "span", className: "all-Font-Sizes", sx: {
                        display: "inline-block",
                        textAlign: "center",
                        fontSize: "12px !important",
                        fontFamily: "Inter",
                        fontWeight: 600,
                        lineHeight: 1.25,
                        color: "inherit",
                    }, children: ["Lead\u2192Placement", _jsx("br", {}), "Conv. Rate"] })),
                width: "140px",
                align: "center",
                sortable: true,
            },
        ],
    },
];
const CLIENT_COLUMNS = [
    { id: "rowLabel", label: "Client", width: "190px", align: "left", sortable: true },
    ...CLIENT_METRIC_COLUMNS,
];
const tableContainerSx = {
    border: "1px solid #EAECF0",
    borderRadius: "12px",
    overflow: "hidden",
    "& table": { borderCollapse: "separate", borderSpacing: 0 },
    "& th, & td": { borderRight: "1px solid #EAECF0", borderBottom: "1px solid #EAECF0" },
    "& tr > :first-of-type": { borderLeft: "1px solid #EAECF0" },
    "& thead tr:first-of-type th": { borderTop: "1px solid #EAECF0" },
    "& thead th, & thead tr:nth-of-type(1) th, & thead tr:nth-of-type(2) th": { background: "#f4f4f5 !important", position: "sticky", zIndex: 6 },
    "& thead tr:nth-of-type(2) th": { top: "45px", zIndex: 5 },
    "& tfoot td, & tfoot th": { backgroundColor: "#f4f4f5", fontWeight: 600 },
    // ── Sticky first column (Outreach Specialist / Client) with Header & Footer ──
    "& thead tr:first-of-type th:first-of-type": {
        position: "sticky",
        left: 0,
        zIndex: "15 !important",
        backgroundColor: "#f4f4f5 !important",
    },
    "& tbody tr:not(.reusable-table-summary-row) td:first-of-type": {
        position: "sticky",
        left: 0,
        zIndex: "4 !important",
        backgroundColor: "#ffffff !important",
    },
    "& tbody tr:not(.reusable-table-summary-row):hover td:first-of-type": {
        backgroundColor: "#F9FAFB !important",
    },
    "& tbody tr.reusable-table-summary-row": {
        position: "sticky",
        bottom: 0,
        zIndex: 8,
        backgroundColor: "#f4f4f5 !important",
    },
    "& tbody tr.reusable-table-summary-row td:first-of-type": {
        position: "sticky",
        left: 0,
        bottom: 0,
        zIndex: "14 !important",
        backgroundColor: "#f4f4f5 !important",
    },
    "& tfoot tr": {
        position: "sticky",
        bottom: 0,
        zIndex: 8,
        backgroundColor: "#f4f4f5 !important",
    },
    "& tfoot tr td:first-of-type, & tfoot tr th:first-of-type": {
        position: "sticky",
        left: 0,
        bottom: 0,
        zIndex: "14 !important",
        backgroundColor: "#f4f4f5 !important",
    },
};
const NumberText = React.memo(({ value, bold, color }) => (_jsx(Box, { sx: { px: 0.1, textAlign: "center" }, children: _jsx(Typography, { className: "all-Font-Sizes", sx: {
            fontSize: "14px !important",
            fontFamily: "Inter",
            fontWeight: bold ? 700 : 400,
            color: color || (bold ? "#181d27" : "#27272a"),
            whiteSpace: "nowrap",
        }, children: value?.toLocaleString() }) })));
const COUNT_FIELD_LABELS = {
    goal_outreach: "Goal",
    actual_outreach: "Actual",
    prev_outreach: "Actual",
    actual_trackers: "Trackers",
    prev_trackers: "Trackers",
};
const ClientListTooltipContent = ({ title, clients, countField }) => (_jsxs(Box, { sx: {
        py: 0.25,
        px: 0.25,
        maxHeight: 240,
        overflowY: "auto",
        minWidth: 150,
        maxWidth: 215,
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
    }, children: [title && (_jsxs(Box, { sx: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5, pb: 0.5, borderBottom: "1px solid rgba(255, 255, 255, 0.15)", gap: 1 }, children: [_jsxs(Typography, { sx: {
                        fontSize: "10.5px",
                        fontWeight: 700,
                        color: "#9ca3af",
                        letterSpacing: "0.4px",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }, children: [title, " (", clients.length, ")"] }), countField && (_jsx(Typography, { sx: {
                        fontSize: "10.5px",
                        fontWeight: 700,
                        color: "#9ca3af",
                        letterSpacing: "0.4px",
                        textTransform: "uppercase",
                        flexShrink: 0,
                        ml: "auto",
                    }, children: COUNT_FIELD_LABELS[countField] }))] })), clients.map((c, i) => {
            const countVal = countField ? c[countField] : undefined;
            return (_jsxs(Box, { className: "all-Font-Sizes-12", sx: {
                    py: 0.3,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.2,
                    borderBottom: i !== clients.length - 1 ? "1px solid rgba(255, 255, 255, 0.08)" : "none",
                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, mr: 0.5 }, children: [_jsx(Typography, { sx: {
                                    fontSize: "12px",
                                    color: "#fff",
                                    lineHeight: 1.3,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: 140,
                                }, children: c.name }), c.status === "inactive" && (_jsx(Box, { component: "sup", sx: { color: "#ef4444", fontWeight: 700, ml: "2px", fontSize: "11px", lineHeight: 0 }, children: "*" }))] }), countField && (_jsx(Typography, { sx: { fontSize: "12px", fontWeight: 600, color: "#fff", flexShrink: 0, ml: "auto" }, children: countVal !== undefined ? countVal.toLocaleString() : 0 }))] }, `tt-${c.id || i}`));
        })] }));
const ActualWithDelta = ({ value, prevValue, diff, color, showDelta, bold, isRate, onClick, hasClick, onPrevClick, hasPrevClick, tooltipContent, prevTooltipContent }) => {
    const formatVal = (v) => (isRate ? pct(v) : (v ?? 0).toLocaleString());
    const hasValueTooltip = Boolean(tooltipContent);
    const valueNode = (_jsx(Typography, { className: "all-Font-Sizes", onClick: hasClick ? onClick : undefined, sx: {
            fontSize: "14px !important",
            fontFamily: "Inter",
            fontWeight: bold ? 700 : (showDelta ? 500 : 400),
            color: color || (bold ? "#181d27" : "#27272a"),
            whiteSpace: "nowrap",
            lineHeight: 1.2,
            cursor: (hasClick || hasValueTooltip) ? "pointer" : "default",
            borderBottom: (hasClick || hasValueTooltip) ? "2px dotted #181d27" : "none",
            pb: (hasClick || hasValueTooltip) ? "1px" : "0px",
            display: (hasClick || hasValueTooltip) ? "inline-block" : "block",
        }, children: formatVal(value) }));
    const wrappedValue = hasValueTooltip ? (_jsx(HoverTooltip, { title: tooltipContent, placement: "top", children: _jsx(Box, { sx: { display: "inline-block" }, children: valueNode }) })) : (valueNode);
    if (showDelta && diff !== undefined) {
        const hasPrevTooltip = Boolean(prevTooltipContent);
        const prevValueNode = (_jsx(Typography, { className: "all-Font-Sizes", onClick: hasPrevClick ? onPrevClick : undefined, sx: {
                fontSize: "12px !important",
                fontFamily: "Inter",
                fontWeight: 400,
                color: "#71717a",
                whiteSpace: "nowrap",
                lineHeight: 1.2,
                mt: 0.25,
                cursor: (hasPrevClick || hasPrevTooltip) ? "pointer" : "default",
                borderBottom: (hasPrevClick || hasPrevTooltip) ? "2px dotted #71717a" : "none",
                pb: (hasPrevClick || hasPrevTooltip) ? "1px" : "0px",
                display: (hasPrevClick || hasPrevTooltip) ? "inline-block" : "block",
            }, children: formatVal(prevValue) }));
        const wrappedPrevValue = hasPrevTooltip ? (_jsx(HoverTooltip, { title: prevTooltipContent, placement: "top", children: _jsx(Box, { sx: { display: "inline-block" }, children: prevValueNode }) })) : (prevValueNode);
        return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", maxWidth: "120px", mx: "auto", px: 0.5 }, children: [_jsxs(Box, { sx: { display: "flex", flexDirection: "column", alignItems: "flex-start" }, children: [wrappedValue, wrappedPrevValue] }), _jsx(VarianceBadge, { value: diff ?? 0, bold: bold, fontSize: "11px" })] }));
    }
    return (_jsx(Box, { sx: { px: 0.1, textAlign: "center" }, children: wrappedValue }));
};
const LeadSentimentTooltipContent = ({ sentiment, totalLeads }) => {
    // Which bucket's addresses are listed below. Hovering a row switches to it; before
    // that it falls back to the first bucket that actually has leads, so the emails are
    // visible without having to go hunting for them.
    const [hoveredBucket, setHoveredBucket] = useState(null);
    const positive = sentiment?.positive ?? 0;
    const neutral = sentiment?.neutral ?? 0;
    const negative = sentiment?.negative ?? 0;
    const baseTotal = totalLeads > 0 ? totalLeads : (positive + neutral + negative > 0 ? positive + neutral + negative : 1);
    const posPct = baseTotal > 0 ? Math.round((positive / baseTotal) * 100) : 0;
    const neuPct = baseTotal > 0 ? Math.round((neutral / baseTotal) * 100) : 0;
    const negPct = baseTotal > 0 ? Math.round((negative / baseTotal) * 100) : 0;
    const BUCKETS = [
        { key: "positive", label: "Positive Leads", color: "#12B76A", count: positive, pct: posPct, emails: sentiment?.emails?.positive ?? [] },
        { key: "neutral", label: "Neutral Leads", color: "#F79009", count: neutral, pct: neuPct, emails: sentiment?.emails?.neutral ?? [] },
        { key: "negative", label: "Negative Leads", color: "#F04438", count: negative, pct: negPct, emails: sentiment?.emails?.negative ?? [] },
    ];
    const activeBucket = BUCKETS.find(b => b.key === hoveredBucket && b.emails.length > 0)
        ?? BUCKETS.find(b => b.emails.length > 0)
        ?? null;
    return (_jsxs(Box, { sx: { p: 0.5, minWidth: 240, maxWidth: 320, color: "#fff" }, children: [_jsxs(Box, { sx: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, pb: 0.5, borderBottom: "1px solid rgba(255, 255, 255, 0.15)" }, children: [_jsx(Typography, { sx: { fontSize: "11px", fontWeight: 700, color: "#9ca3af", letterSpacing: "0.5px", textTransform: "uppercase" }, children: "LEAD SENTIMENT" }), _jsxs(Typography, { sx: { fontSize: "11px", fontWeight: 500, color: "#9ca3af" }, children: [totalLeads, " lead", totalLeads === 1 ? "" : "s"] })] }), _jsx(Box, { sx: { display: "flex", height: "6px", borderRadius: "3px", overflow: "hidden", gap: "3px", mb: 1.5 }, children: (positive > 0 || neutral > 0 || negative > 0) ? (_jsxs(_Fragment, { children: [positive > 0 && _jsx(Box, { sx: { flex: positive, backgroundColor: "#12B76A", borderRadius: "3px" } }), neutral > 0 && _jsx(Box, { sx: { flex: neutral, backgroundColor: "#F79009", borderRadius: "3px" } }), negative > 0 && _jsx(Box, { sx: { flex: negative, backgroundColor: "#F04438", borderRadius: "3px" } })] })) : (_jsx(Box, { sx: { flex: 1, backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "3px" } })) }), _jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.75 }, children: BUCKETS.map(b => {
                    const selectable = b.emails.length > 0;
                    const isActive = activeBucket?.key === b.key;
                    return (_jsxs(Box, { onMouseEnter: selectable ? () => setHoveredBucket(b.key) : undefined, sx: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: selectable ? "pointer" : "default",
                            borderRadius: "4px",
                            mx: -0.5,
                            px: 0.5,
                            py: 0.25,
                            backgroundColor: isActive ? "rgba(255, 255, 255, 0.08)" : "transparent",
                        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Box, { sx: { width: 7, height: 7, borderRadius: "50%", backgroundColor: b.color } }), _jsx(Typography, { sx: { fontSize: "12px", color: isActive ? "#fff" : "#d1d5db" }, children: b.label })] }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: "12px", fontWeight: 600, color: "#fff" }, children: b.count }), _jsxs(Typography, { sx: { fontSize: "11px", color: "#9ca3af" }, children: ["(", b.pct, "%)"] })] })] }, b.key));
                }) }), activeBucket && (_jsxs(Box, { sx: { mt: 1.25, pt: 0.75, borderTop: "1px solid rgba(255, 255, 255, 0.15)" }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, mb: 0.75 }, children: [_jsx(Box, { sx: { width: 7, height: 7, borderRadius: "50%", backgroundColor: activeBucket.color, flexShrink: 0 } }), _jsxs(Typography, { sx: { fontSize: "10.5px", fontWeight: 700, color: activeBucket.color, letterSpacing: "0.4px", textTransform: "uppercase" }, children: [activeBucket.label, " (", activeBucket.emails.length, ")"] })] }), _jsx(Box, { sx: {
                            maxHeight: 160,
                            overflowY: "auto",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                        }, children: activeBucket.emails.map(email => (_jsx(Typography, { sx: {
                                fontSize: "11.5px",
                                color: "#e5e7eb",
                                lineHeight: 1.5,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }, children: email }, email))) })] }))] }));
};
const LeadSentimentCell = ({ value, prevValue, diff, color, showDelta, sentiment, prevSentiment, bold }) => {
    const hasSentiment = Boolean(value > 0);
    const valueNode = (_jsx(Typography, { className: "all-Font-Sizes", sx: {
            fontSize: "14px !important",
            fontFamily: "Inter",
            fontWeight: bold ? 700 : 500,
            color: color || (bold ? "#181d27" : "#27272a"),
            whiteSpace: "nowrap",
            lineHeight: 1.2,
            cursor: hasSentiment ? "pointer" : "default",
            borderBottom: hasSentiment ? "2px dotted #181d27" : "none",
            pb: hasSentiment ? "1px" : "0px",
            display: "inline-block",
        }, children: (value ?? 0).toLocaleString() }));
    const wrappedValue = hasSentiment ? (_jsx(HoverTooltip, { title: _jsx(LeadSentimentTooltipContent, { sentiment: sentiment, totalLeads: value }), placement: "top", children: _jsx(Box, { sx: { display: "inline-block" }, children: valueNode }) })) : (valueNode);
    if (showDelta && diff !== undefined) {
        const hasPrevSentiment = Boolean((prevValue ?? 0) > 0);
        const prevValueNode = (_jsx(Typography, { className: "all-Font-Sizes", sx: {
                fontSize: "12px !important",
                fontFamily: "Inter",
                fontWeight: 400,
                color: "#71717a",
                whiteSpace: "nowrap",
                lineHeight: 1.2,
                mt: 0.25,
                cursor: hasPrevSentiment ? "pointer" : "default",
                borderBottom: hasPrevSentiment ? "2px dotted #71717a" : "none",
                pb: hasPrevSentiment ? "1px" : "0px",
                display: "inline-block",
            }, children: (prevValue ?? 0).toLocaleString() }));
        const wrappedPrevValue = hasPrevSentiment ? (_jsx(HoverTooltip, { title: _jsx(LeadSentimentTooltipContent, { sentiment: prevSentiment, totalLeads: prevValue ?? 0 }), placement: "top", children: _jsx(Box, { sx: { display: "inline-block" }, children: prevValueNode }) })) : (prevValueNode);
        return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", maxWidth: "120px", mx: "auto", px: 0.5 }, children: [_jsxs(Box, { sx: { display: "flex", flexDirection: "column", alignItems: "flex-start" }, children: [wrappedValue, wrappedPrevValue] }), _jsx(VarianceBadge, { value: diff ?? 0, bold: bold, fontSize: "11px" })] }));
    }
    return (_jsx(Box, { sx: { px: 0.1, textAlign: "center" }, children: wrappedValue }));
};
// Explains the Enrollments VAR badge: what was in the trackers vs what actually
// enrolled, and the gap between the two.
const EnrollmentsReachTooltipContent = ({ totalContacts, enrolled }) => {
    const gap = totalContacts - enrolled;
    const rows = [
        { label: "Contacts in trackers", value: totalContacts },
        { label: "Enrolled", value: enrolled },
        { label: "Not reached", value: gap, muted: true },
    ];
    return (_jsxs(Box, { sx: { p: 0.5, minWidth: 200, color: "#fff" }, children: [_jsx(Typography, { sx: { fontSize: "11px", fontWeight: 700, color: "#9ca3af", letterSpacing: "0.5px", textTransform: "uppercase", mb: 1, pb: 0.5, borderBottom: "1px solid rgba(255, 255, 255, 0.15)" }, children: "Enrollment Reach" }), _jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.75 }, children: rows.map((r) => (_jsxs(Box, { sx: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }, children: [_jsx(Typography, { sx: { fontSize: "12px", color: r.muted ? "#9ca3af" : "#d1d5db" }, children: r.label }), _jsx(Typography, { sx: { fontSize: "12px", fontWeight: 600, color: r.muted ? "#9ca3af" : "#fff" }, children: r.value.toLocaleString() })] }, r.label))) }), _jsx(Typography, { sx: { fontSize: "11px", color: "#9ca3af", mt: 1, pt: 0.75, borderTop: "1px solid rgba(255, 255, 255, 0.15)" }, children: "Unsynced, bounced or duplicate contacts" })] }));
};
const renderMetricCell = (metrics, colId, accentColor, showDelta, onPlacementClick, onPrevPlacementClick, bold, outreachTooltipContent, outreachPrevTooltipContent, trackersTooltipContent, trackersPrevTooltipContent) => {
    switch (colId) {
        case "trackersCount":
            return (_jsx(ActualWithDelta, { value: metrics.trackers?.actual_trackers ?? 0, prevValue: metrics.trackers?.prev_trackers, diff: metrics.trackers?.trackers_diff, color: accentColor, showDelta: showDelta, bold: bold, tooltipContent: trackersTooltipContent, prevTooltipContent: trackersPrevTooltipContent }));
        case "outreachActual":
            return (_jsx(ActualWithDelta, { value: metrics.outreach.actual_outreach ?? 0, prevValue: metrics.outreach.prev_outreach, diff: metrics.outreach.outreach_diff, color: accentColor, showDelta: showDelta, bold: bold, tooltipContent: outreachTooltipContent, prevTooltipContent: outreachPrevTooltipContent }));
        case "enrollmentsReach": {
            const totalContacts = metrics.outreach.total_contacts ?? 0;
            const enrolled = metrics.outreach.actual_outreach ?? 0;
            // A share of the tracker list, not a movement — so it renders as a flat
            // amber pill with no trend arrow, since up/down means nothing here.
            const badge = _jsx(VarianceBadge, { value: metrics.outreach.reach_pct ?? 0, neutral: true, bold: bold });
            return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: totalContacts > 0 ? (_jsx(HoverTooltip, { title: _jsx(EnrollmentsReachTooltipContent, { totalContacts: totalContacts, enrolled: enrolled }), placement: "top", children: _jsx(Box, { sx: { display: "inline-flex", cursor: "pointer" }, children: badge }) })) : (badge) }));
        }
        case "leadsActual":
            return (_jsx(LeadSentimentCell, { value: metrics.leads.actual_lead ?? 0, prevValue: metrics.leads.prev_lead, diff: metrics.leads.lead_diff, color: accentColor, showDelta: showDelta, sentiment: metrics.leads.sentiment, prevSentiment: metrics.leads.prev_sentiment, bold: bold }));
        case "placementsGoal": return _jsx(NumberText, { value: metrics.placements?.goal_placement ?? 0, bold: bold });
        case "placementsVar":
            return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(VarianceBadge, { value: metrics.placements?.per_goal ?? 0, bold: bold }) }));
        case "placementsPlaced": {
            const placedVal = metrics.placements?.actual_placement ?? metrics.placementsCount ?? 0;
            const prevPlacedVal = metrics.placements?.prev_placement ?? 0;
            const hasPlacements = Boolean(placedVal > 0 && onPlacementClick);
            const hasPrevPlacements = Boolean(prevPlacedVal > 0 && onPrevPlacementClick);
            return (_jsx(ActualWithDelta, { value: placedVal, prevValue: prevPlacedVal, diff: metrics.placements?.placement_diff, color: accentColor, showDelta: showDelta, bold: bold, onClick: onPlacementClick, hasClick: hasPlacements, onPrevClick: onPrevPlacementClick, hasPrevClick: hasPrevPlacements }));
        }
        case "conversionOutLead":
            return (_jsx(ActualWithDelta, { value: metrics.conversion.out_lead ?? 0, prevValue: metrics.conversion.prev_out_lead, diff: metrics.conversion.out_lead_diff, color: accentColor, showDelta: showDelta, bold: bold, isRate: true }));
        case "conversionLeadPlace":
            return (_jsx(ActualWithDelta, { value: metrics.conversion.lead_place ?? 0, prevValue: metrics.conversion.prev_lead_place, diff: metrics.conversion.lead_place_diff, color: accentColor, showDelta: showDelta, bold: bold, isRate: true }));
        default: return null;
    }
};
// "# of Trackers" hover: which clients the row's trackers belong to, and how many each.
const buildTrackerTooltips = (trackers, isComparing) => {
    const clients = trackers?.clients ?? [];
    const currentList = clients
        .filter(c => (c.actual_trackers ?? 0) > 0)
        .sort((a, b) => (b.actual_trackers ?? 0) - (a.actual_trackers ?? 0) || (a.name || "").localeCompare(b.name || ""));
    const prevList = clients
        .filter(c => (c.prev_trackers ?? 0) > 0)
        .sort((a, b) => (b.prev_trackers ?? 0) - (a.prev_trackers ?? 0) || (a.name || "").localeCompare(b.name || ""));
    return {
        tooltip: currentList.length > 0
            ? _jsx(ClientListTooltipContent, { title: isComparing ? "Current" : "Clients", clients: currentList, countField: "actual_trackers" })
            : undefined,
        prevTooltip: isComparing && prevList.length > 0
            ? _jsx(ClientListTooltipContent, { title: "Prev", clients: prevList, countField: "prev_trackers" })
            : undefined,
    };
};
const isDemoClient = (name) => {
    if (!name)
        return false;
    return /demo/i.test(name.trim());
};
export default function OutreachPerformance() {
    const dispatch = useDispatch();
    const { groupBy, startDate, endDate, compareStartDate, compareEndDate, loading, } = useSelector((state) => state.outreachPerformance);
    const entities = useSelector(selectOutreachPerformanceEntities);
    const teamTotal = useSelector(selectOutreachPerformanceTeamTotal);
    const bySpecialistData = useSelector(selectOutreachPerformanceBySpecialist);
    const specialists = useSelector(selectOutreachPerformanceSpecialists);
    const lastUpdated = useSelector(selectOutreachPerformanceLastUpdated);
    const canManagePlacements = useSelector(selectCanManagePlacements);
    const canRefresh = useSelector(selectCanRefreshOutreach);
    // Add Published Article opens the Publisher Reporting drawer (it uses the Publisher Reporting APIs), so it needs
    // that module's "Manage Placements" action
    const canAddPlacement = useSelector(selectCanManagePublisherReportingPlacements);
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    const allClients = useSelector(selectAllClientList);
    const [selectedSpecialist, setSelectedSpecialist] = useState("");
    const [addArticleOpen, setAddArticleOpen] = useState(false);
    const [addPlacementOpen, setAddPlacementOpen] = useState(false);
    const [publishedArticlesModalOpen, setPublishedArticlesModalOpen] = useState(false);
    const [articlesFilter, setArticlesFilter] = useState({});
    const [syncingLeads, setSyncingLeads] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        columnId: "outreachActual",
        direction: "desc",
    });
    const specialistOptions = useMemo(() => {
        let list = specialists;
        if ((!list || !list.length) && bySpecialistData?.entities?.length) {
            list = bySpecialistData.entities.map(s => ({ id: s.id, name: s.name }));
        }
        return [
            { value: "", label: "All Outreach Specialists" },
            ...(list || []).map(s => ({
                value: s.id,
                label: s.name,
            })).sort((a, b) => {
                if (a.value === "")
                    return -1;
                if (b.value === "")
                    return 1;
                return a.label.localeCompare(b.label);
            }),
        ];
    }, [specialists, bySpecialistData]);
    const handleSortChange = useCallback((columnId, direction) => {
        if (!direction) {
            setSortConfig(null);
        }
        else {
            setSortConfig({ columnId, direction });
        }
    }, []);
    const visibleEntities = useMemo(() => {
        if (!entities || !entities.length)
            return [];
        if (groupBy === "client") {
            if (selectedSpecialist) {
                const spec = bySpecialistData?.entities?.find(s => s.id === selectedSpecialist);
                if (!spec)
                    return [];
                const assignedList = (spec.assigned_clients || []).filter(c => !isDemoClient(c.name));
                return assignedList.map(c => {
                    const goalOut = c.goal_outreach ?? 0;
                    const actOut = c.actual_outreach ?? 0;
                    const prevOut = c.prev_outreach ?? 0;
                    const goalLead = c.goal_lead ?? 0;
                    const actLead = c.actual_lead ?? 0;
                    const prevLead = c.prev_lead ?? 0;
                    const actPlacement = c.actual_placement ?? 0;
                    const prevPlacement = c.prev_placement ?? 0;
                    const goalPlacement = c.goal_placement ?? 0;
                    const actTrackers = c.actual_trackers ?? 0;
                    const prevTrackers = c.prev_trackers ?? 0;
                    return {
                        id: c.id,
                        name: c.name,
                        status: c.status || "active",
                        is_assigned: c.is_assigned ?? (c.is_assigned_current || c.is_assigned_prev),
                        is_assigned_current: c.is_assigned_current,
                        is_assigned_prev: c.is_assigned_prev,
                        is_current: c.is_current ?? (c.is_assigned_current || actOut > 0 || actLead > 0 || actPlacement > 0 || goalOut > 0),
                        is_prev: Boolean(c.is_assigned_prev || c.is_prev || prevOut > 0 || prevLead > 0 || prevPlacement > 0 || (c.prev_goal_outreach ?? 0) > 0),
                        trackers: {
                            actual_trackers: actTrackers,
                            prev_trackers: prevTrackers,
                            trackers_diff: c.trackers_diff ?? (prevTrackers > 0 ? Math.round(((actTrackers - prevTrackers) / prevTrackers) * 100) : 0),
                            clients: actTrackers > 0 || prevTrackers > 0
                                ? [{ id: c.id, name: c.name, status: c.status, actual_trackers: actTrackers, prev_trackers: prevTrackers }]
                                : [],
                        },
                        outreach: {
                            goal_outreach: goalOut,
                            actual_outreach: actOut,
                            prev_outreach: prevOut,
                            outreach_diff: c.outreach_diff ?? (prevOut > 0 ? Math.round(((actOut - prevOut) / prevOut) * 100) : 0),
                            per_goal: c.per_goal ?? (goalOut > 0 ? Math.round(((actOut - goalOut) / goalOut) * 100) : 0),
                            total_contacts: c.total_contacts ?? 0,
                            prev_total_contacts: c.prev_total_contacts ?? 0,
                            reach_pct: c.reach_pct ?? ((c.total_contacts ?? 0) > 0 ? (actOut / (c.total_contacts ?? 0)) * 100 : 0),
                        },
                        leads: {
                            goal_lead: goalLead,
                            actual_lead: actLead,
                            prev_lead: prevLead,
                            lead_diff: c.lead_diff ?? (prevLead > 0 ? Math.round(((actLead - prevLead) / prevLead) * 100) : 0),
                            per_goal: c.lead_per_goal ?? (goalLead > 0 ? Math.round(((actLead - goalLead) / goalLead) * 100) : 0),
                            sentiment: c.sentiment || { positive: 0, neutral: 0, negative: 0, total: 0 },
                            prev_sentiment: c.prev_sentiment || { positive: 0, neutral: 0, negative: 0, total: 0 },
                        },
                        placements_count: {
                            goal_placement: goalPlacement,
                            prev_goal_placement: c.prev_goal_placement ?? 0,
                            actual_placement: actPlacement,
                            prev_placement: prevPlacement,
                            placement_diff: c.placement_diff ?? (prevPlacement > 0 ? Math.round(((actPlacement - prevPlacement) / prevPlacement) * 100) : 0),
                            per_goal: c.placement_per_goal ?? (goalPlacement > 0 ? Math.round(((actPlacement - goalPlacement) / goalPlacement) * 100) : 0),
                        },
                        conversion: {
                            out_lead: c.out_lead ?? (actOut > 0 ? (actLead / actOut) * 100 : 0),
                            prev_out_lead: c.prev_out_lead ?? (prevOut > 0 ? (prevLead / prevOut) * 100 : 0),
                            out_lead_diff: c.out_lead_diff ?? 0,
                            lead_place: c.lead_place ?? (actLead > 0 ? (actPlacement / actLead) * 100 : 0),
                            prev_lead_place: c.prev_lead_place ?? (prevLead > 0 ? (prevPlacement / prevLead) * 100 : 0),
                            lead_place_diff: c.lead_place_diff ?? 0,
                        },
                    };
                });
            }
            return entities.filter(e => !isDemoClient(e.name));
        }
        return entities.map(e => ({
            ...e,
            assigned_clients: (e.assigned_clients || []).filter(c => !isDemoClient(c.name)),
            clients: (e.clients || []).filter(c => !isDemoClient(c.name)),
            current_clients: (e.current_clients || []).filter(c => !isDemoClient(c.name)),
            prev_clients: (e.prev_clients || []).filter(c => !isDemoClient(c.name)),
        }));
    }, [entities, groupBy, selectedSpecialist, bySpecialistData]);
    const computedTeamTotal = useMemo(() => {
        if (!teamTotal)
            return null;
        if (groupBy === "client") {
            const totals = visibleEntities.reduce((acc, row) => {
                acc.total_contacts += row.outreach.total_contacts ?? 0;
                acc.prev_total_contacts += row.outreach.prev_total_contacts ?? 0;
                acc.actual_trackers += row.trackers?.actual_trackers ?? 0;
                acc.prev_trackers += row.trackers?.prev_trackers ?? 0;
                acc.goal_placement += row.placements_count?.goal_placement ?? 0;
                acc.prev_goal_placement += row.placements_count?.prev_goal_placement ?? 0;
                acc.goal_outreach += row.outreach.goal_outreach ?? 0;
                acc.actual_outreach += row.outreach.actual_outreach ?? 0;
                acc.prev_outreach += row.outreach.prev_outreach ?? 0;
                acc.goal_lead += row.leads.goal_lead ?? 0;
                acc.actual_lead += row.leads.actual_lead ?? 0;
                acc.prev_lead += row.leads.prev_lead ?? 0;
                acc.actual_placement += (row.placements_count?.actual_placement || row.placements?.length || 0);
                acc.prev_placement += (row.placements_count?.prev_placement || 0);
                acc.positive_lead += (row.leads.sentiment?.positive || 0);
                acc.neutral_lead += (row.leads.sentiment?.neutral || 0);
                acc.negative_lead += (row.leads.sentiment?.negative || 0);
                acc.prev_positive_lead += (row.leads.prev_sentiment?.positive || 0);
                acc.prev_neutral_lead += (row.leads.prev_sentiment?.neutral || 0);
                acc.prev_negative_lead += (row.leads.prev_sentiment?.negative || 0);
                SENTIMENT_KEYS.forEach(k => {
                    acc.emails[k].push(...(row.leads.sentiment?.emails?.[k] || []));
                    acc.prev_emails[k].push(...(row.leads.prev_sentiment?.emails?.[k] || []));
                });
                return acc;
            }, {
                total_contacts: 0, prev_total_contacts: 0,
                actual_trackers: 0, prev_trackers: 0,
                goal_placement: 0, prev_goal_placement: 0,
                goal_outreach: 0, actual_outreach: 0, prev_outreach: 0,
                goal_lead: 0, actual_lead: 0, prev_lead: 0,
                actual_placement: 0, prev_placement: 0,
                positive_lead: 0, neutral_lead: 0, negative_lead: 0,
                prev_positive_lead: 0, prev_neutral_lead: 0, prev_negative_lead: 0,
                emails: { positive: [], neutral: [], negative: [] },
                prev_emails: { positive: [], neutral: [], negative: [] },
            });
            const calcPercentage = (actual, comp) => comp > 0 ? Math.round(((actual - comp) / comp) * 100) : 0;
            const team_out_lead = totals.actual_outreach > 0 ? (totals.actual_lead / totals.actual_outreach) * 100 : 0;
            const team_prev_out_lead = totals.prev_outreach > 0 ? (totals.prev_lead / totals.prev_outreach) * 100 : 0;
            const team_out_lead_diff = calcPercentage(team_out_lead, team_prev_out_lead);
            const team_lead_place = totals.actual_lead > 0 ? (totals.actual_placement / totals.actual_lead) * 100 : 0;
            const team_prev_lead_place = totals.prev_lead > 0 ? (totals.prev_placement / totals.prev_lead) * 100 : 0;
            const team_lead_place_diff = calcPercentage(team_lead_place, team_prev_lead_place);
            const currentClientsCount = visibleEntities.filter(e => e.is_current !== false).length;
            const prevClientsCount = visibleEntities.filter(e => Boolean(e.is_prev)).length;
            return {
                client_count: currentClientsCount,
                prev_client_count: prevClientsCount,
                trackers: {
                    actual_trackers: totals.actual_trackers,
                    prev_trackers: totals.prev_trackers,
                    trackers_diff: calcPercentage(totals.actual_trackers, totals.prev_trackers),
                    clients: visibleEntities.flatMap(row => row.trackers?.clients ?? []),
                },
                outreach: {
                    goal_outreach: Math.round(totals.goal_outreach),
                    actual_outreach: totals.actual_outreach,
                    prev_outreach: totals.prev_outreach,
                    outreach_diff: calcPercentage(totals.actual_outreach, totals.prev_outreach),
                    per_goal: calcPercentage(totals.actual_outreach, totals.goal_outreach),
                    total_contacts: totals.total_contacts,
                    prev_total_contacts: totals.prev_total_contacts,
                    reach_pct: totals.total_contacts > 0 ? (totals.actual_outreach / totals.total_contacts) * 100 : 0,
                },
                leads: {
                    goal_lead: Math.round(totals.goal_lead),
                    actual_lead: totals.actual_lead,
                    prev_lead: totals.prev_lead,
                    lead_diff: calcPercentage(totals.actual_lead, totals.prev_lead),
                    per_goal: calcPercentage(totals.actual_lead, totals.goal_lead),
                    sentiment: {
                        positive: totals.positive_lead,
                        neutral: totals.neutral_lead,
                        negative: totals.negative_lead,
                        total: totals.actual_lead,
                        emails: totals.emails,
                    },
                    prev_sentiment: {
                        positive: totals.prev_positive_lead,
                        neutral: totals.prev_neutral_lead,
                        negative: totals.prev_negative_lead,
                        total: totals.prev_lead,
                        emails: totals.prev_emails,
                    },
                },
                placements: {
                    goal_placement: Math.round(totals.goal_placement),
                    prev_goal_placement: Math.round(totals.prev_goal_placement),
                    actual_placement: totals.actual_placement,
                    prev_placement: totals.prev_placement,
                    placement_diff: calcPercentage(totals.actual_placement, totals.prev_placement),
                    per_goal: calcPercentage(totals.actual_placement, totals.goal_placement),
                    total: totals.actual_placement,
                },
                conversion: {
                    out_lead: team_out_lead,
                    prev_out_lead: team_prev_out_lead,
                    out_lead_diff: team_out_lead_diff,
                    lead_place: team_lead_place,
                    prev_lead_place: team_prev_lead_place,
                    lead_place_diff: team_lead_place_diff,
                },
            };
        }
        const uniqueClientIds = new Set();
        visibleEntities.forEach(e => {
            e.clients?.forEach(c => uniqueClientIds.add(c.id));
        });
        const totalAssignedClientCount = visibleEntities.reduce((acc, row) => acc + (row.assigned_client_count ?? row.assigned_clients?.length ?? 0), 0);
        return {
            ...teamTotal,
            client_count: uniqueClientIds.size,
            assigned_client_count: totalAssignedClientCount,
        };
    }, [teamTotal, visibleEntities, groupBy]);
    const sortedRows = useMemo(() => {
        if (!visibleEntities || !visibleEntities.length)
            return [];
        if (!sortConfig || !sortConfig.columnId || !sortConfig.direction)
            return visibleEntities;
        const { columnId, direction } = sortConfig;
        const modifier = direction === "asc" ? 1 : -1;
        return [...visibleEntities].sort((a, b) => {
            let valA = 0;
            let valB = 0;
            switch (columnId) {
                case "rowLabel":
                    valA = (a.name || "").toLowerCase();
                    valB = (b.name || "").toLowerCase();
                    break;
                case "trackersCount":
                    valA = a.trackers?.actual_trackers ?? 0;
                    valB = b.trackers?.actual_trackers ?? 0;
                    break;
                case "outreachActual":
                    valA = a.outreach.actual_outreach ?? 0;
                    valB = b.outreach.actual_outreach ?? 0;
                    break;
                case "enrollmentsReach":
                    valA = a.outreach.reach_pct ?? 0;
                    valB = b.outreach.reach_pct ?? 0;
                    break;
                case "leadsActual":
                    valA = a.leads.actual_lead ?? 0;
                    valB = b.leads.actual_lead ?? 0;
                    break;
                case "placementsGoal":
                    valA = a.placements_count?.goal_placement ?? 0;
                    valB = b.placements_count?.goal_placement ?? 0;
                    break;
                case "placementsPlaced":
                    valA = a.placements_count?.actual_placement ?? a.placements?.length ?? 0;
                    valB = b.placements_count?.actual_placement ?? b.placements?.length ?? 0;
                    break;
                case "placementsVar":
                    valA = a.placements_count?.per_goal ?? 0;
                    valB = b.placements_count?.per_goal ?? 0;
                    break;
                case "conversionOutLead":
                    valA = a.conversion.out_lead ?? 0;
                    valB = b.conversion.out_lead ?? 0;
                    break;
                case "conversionLeadPlace":
                    valA = a.conversion.lead_place ?? 0;
                    valB = b.conversion.lead_place ?? 0;
                    break;
                default:
                    return 0;
            }
            if (typeof valA === "string" && typeof valB === "string") {
                return modifier * valA.localeCompare(valB);
            }
            return modifier * ((Number(valA) || 0) - (Number(valB) || 0));
        });
    }, [visibleEntities, sortConfig, groupBy]);
    const dateRangeValue = useMemo(() => {
        if (!startDate || !endDate)
            return undefined;
        try {
            return {
                start: parseDate(startDate),
                end: parseDate(endDate),
            };
        }
        catch {
            return undefined;
        }
    }, [startDate, endDate]);
    const compareRangeValue = useMemo(() => {
        if (!compareStartDate || !compareEndDate)
            return undefined;
        try {
            return {
                start: parseDate(compareStartDate),
                end: parseDate(compareEndDate),
            };
        }
        catch {
            return undefined;
        }
    }, [compareStartDate, compareEndDate]);
    const doFetch = useCallback(() => {
        dispatch(fetchOutreachPerformance({
            start_date: startDate,
            end_date: endDate,
            compare_start_date: compareStartDate || undefined,
            compare_end_date: compareEndDate || undefined,
            group_by: groupBy,
        }));
    }, [dispatch, startDate, endDate, compareStartDate, compareEndDate]);
    useEffect(() => {
        doFetch();
    }, [doFetch]);
    const handleApplyDateRange = useCallback((data) => {
        if (data.mainRange) {
            dispatch(setDateRange({
                startDate: data.mainRange.start.toString(),
                endDate: data.mainRange.end.toString(),
            }));
        }
        dispatch(setCompareDateRange({
            compareStartDate: data.compareRange ? data.compareRange.start.toString() : "",
            compareEndDate: data.compareRange ? data.compareRange.end.toString() : "",
        }));
    }, [dispatch]);
    const handleGroupByToggle = useCallback((_, checked) => {
        dispatch(setGroupBy(checked ? "specialist" : "client"));
    }, [dispatch]);
    const handleAddArticle = useCallback(() => {
        setAddArticleOpen(true);
    }, []);
    const handleAddPlacement = useCallback(() => {
        // the drawer reads the clients / users from the store but does not fetch them itself
        if (!designationWiseUsers)
            dispatch(fetchDesignationWiseUsers());
        if (!allClients?.length)
            dispatch(fetchAllClientList());
        setAddPlacementOpen(true);
    }, [dispatch, designationWiseUsers, allClients]);
    const handleArticleSaved = useCallback(() => {
        doFetch();
    }, [doFetch]);
    const handleExport = useCallback(() => {
        exportOutreachPerformance({
            entities: sortedRows,
            teamTotal: computedTeamTotal,
            startDate,
            endDate,
            compareStartDate: compareStartDate || undefined,
            compareEndDate: compareEndDate || undefined,
            groupBy,
        });
    }, [sortedRows, computedTeamTotal, startDate, endDate, compareStartDate, compareEndDate, groupBy]);
    const handleOpenArticlesModal = useCallback((filter) => {
        setArticlesFilter(filter);
        setPublishedArticlesModalOpen(true);
    }, []);
    const handleSyncLeads = useCallback(async () => {
        try {
            setSyncingLeads(true);
            await dispatch(syncOutreachLeads({ startDate, endDate })).unwrap();
            doFetch();
        }
        catch {
            // Error handled by redux
        }
        finally {
            setSyncingLeads(false);
        }
    }, [dispatch, startDate, endDate, doFetch]);
    const isComparing = Boolean(compareStartDate && compareEndDate);
    const summaryAssignedClients = useMemo(() => {
        const map = new Map();
        entities.forEach(e => {
            (e.current_assigned_clients ?? (e.assigned_clients ?? []).filter(c => c.is_assigned_current !== false && c.is_assigned !== false)).forEach(c => {
                if (map.has(c.id)) {
                    const existing = map.get(c.id);
                    existing.goal_outreach = (existing.goal_outreach || 0) + (c.goal_outreach || 0);
                }
                else {
                    map.set(c.id, { ...c });
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => (b.goal_outreach || 0) - (a.goal_outreach || 0) || (a.name || "").localeCompare(b.name || ""));
    }, [entities]);
    const summaryPrevAssignedClients = useMemo(() => {
        const map = new Map();
        entities.forEach(e => {
            (e.prev_assigned_clients ?? (e.assigned_clients ?? []).filter(c => c.is_assigned_prev)).forEach(c => {
                const gOut = c.prev_goal_outreach ?? c.goal_outreach ?? 0;
                if (map.has(c.id)) {
                    const existing = map.get(c.id);
                    existing.goal_outreach = (existing.goal_outreach || 0) + gOut;
                }
                else {
                    map.set(c.id, { ...c, goal_outreach: gOut });
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => (b.goal_outreach || 0) - (a.goal_outreach || 0) || (a.name || "").localeCompare(b.name || ""));
    }, [entities]);
    const summaryCurrentClients = useMemo(() => {
        const map = new Map();
        entities.forEach(e => {
            const list = e.current_clients ?? e.clients?.filter(c => c.is_current !== false) ?? e.clients ?? [];
            list.forEach(c => {
                if (map.has(c.id)) {
                    const existing = map.get(c.id);
                    existing.actual_outreach = (existing.actual_outreach || 0) + (c.actual_outreach || 0);
                }
                else {
                    map.set(c.id, { ...c });
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => (b.actual_outreach || 0) - (a.actual_outreach || 0) || (a.name || "").localeCompare(b.name || ""));
    }, [entities]);
    const summaryPrevClients = useMemo(() => {
        const map = new Map();
        entities.forEach(e => {
            const list = e.prev_clients ?? e.clients?.filter(c => c.is_prev) ?? [];
            list.forEach(c => {
                if (map.has(c.id)) {
                    const existing = map.get(c.id);
                    existing.prev_outreach = (existing.prev_outreach || 0) + (c.prev_outreach || 0);
                }
                else {
                    map.set(c.id, { ...c });
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => (b.prev_outreach || 0) - (a.prev_outreach || 0) || (a.name || "").localeCompare(b.name || ""));
    }, [entities]);
    const summaryAllClients = useMemo(() => {
        const map = new Map();
        entities.forEach(e => {
            (e.clients ?? []).forEach(c => {
                if (map.has(c.id)) {
                    const existing = map.get(c.id);
                    existing.actual_outreach = (existing.actual_outreach || 0) + (c.actual_outreach || 0);
                }
                else {
                    map.set(c.id, { ...c });
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => (b.actual_outreach || 0) - (a.actual_outreach || 0) || (a.name || "").localeCompare(b.name || ""));
    }, [entities]);
    const renderSpecialistCell = useCallback((row, col) => {
        const currentAssignedClients = row.current_assigned_clients ?? (row.assigned_clients ?? []).filter(c => c.is_assigned_current !== false && c.is_assigned !== false);
        const assignedCount = row.assigned_client_count ?? currentAssignedClients.length;
        const prevAssignedClients = row.prev_assigned_clients ?? (row.assigned_clients ?? []).filter(c => c.is_assigned_prev);
        const prevAssignedCount = row.prev_assigned_client_count ?? prevAssignedClients.length;
        const currentClients = row.current_clients ?? row.clients?.filter(c => c.is_current !== false) ?? row.clients ?? [];
        const prevClients = row.prev_clients ?? row.clients?.filter(c => c.is_prev) ?? [];
        const allClients = row.clients ?? [];
        if (col.id === "rowLabel") {
            const hasAssignedTooltip = assignedCount > 0 && currentAssignedClients.length > 0;
            const hasPrevAssignedTooltip = isComparing && prevAssignedCount > 0 && prevAssignedClients.length > 0;
            const currentSubtext = (_jsx(Typography, { className: "all-Font-Sizes", sx: {
                    fontSize: "12px !important",
                    color: "#71717A",
                    borderBottom: hasAssignedTooltip ? "2px dotted #71717A" : "none",
                    cursor: hasAssignedTooltip ? "pointer" : "default",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    "&:hover": hasAssignedTooltip ? { color: "#181d27" } : {},
                }, children: isComparing ? `Current (${assignedCount})` : `Clients (${assignedCount})` }));
            const prevSubtext = (_jsxs(Typography, { className: "all-Font-Sizes", sx: {
                    fontSize: "12px !important",
                    color: "#71717A",
                    borderBottom: hasPrevAssignedTooltip ? "2px dotted #71717A" : "none",
                    cursor: hasPrevAssignedTooltip ? "pointer" : "default",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    "&:hover": hasPrevAssignedTooltip ? { color: "#181d27" } : {},
                }, children: ["Prev (", prevAssignedCount, ")"] }));
            return (_jsxs(Box, { sx: { px: 0.1 }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontWeight: 400, fontSize: "14px !important", color: "#27272a" }, children: row.name }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap", mt: "1px" }, children: [hasAssignedTooltip ? (_jsx(HoverTooltip, { title: _jsx(ClientListTooltipContent, { title: isComparing ? "Current" : "Assigned", clients: currentAssignedClients, countField: "goal_outreach" }), placement: "bottom-start", children: _jsx(Box, { sx: { display: "inline-block" }, children: currentSubtext }) })) : (currentSubtext), isComparing && (hasPrevAssignedTooltip ? (_jsx(HoverTooltip, { title: _jsx(ClientListTooltipContent, { title: "Prev", clients: prevAssignedClients.map(c => ({ ...c, goal_outreach: c.prev_goal_outreach ?? c.goal_outreach })), countField: "goal_outreach" }), placement: "bottom-start", children: _jsx(Box, { sx: { display: "inline-block" }, children: prevSubtext }) })) : (prevSubtext))] })] }));
        }
        const outreachTooltip = isComparing
            ? (currentClients.length > 0 ? _jsx(ClientListTooltipContent, { title: "Current", clients: currentClients, countField: "actual_outreach" }) : undefined)
            : (allClients.length > 0 ? _jsx(ClientListTooltipContent, { title: "Clients", clients: allClients, countField: "actual_outreach" }) : undefined);
        const outreachPrevTooltip = isComparing && prevClients.length > 0
            ? _jsx(ClientListTooltipContent, { title: "Prev", clients: prevClients, countField: "prev_outreach" })
            : undefined;
        const trackerTooltips = buildTrackerTooltips(row.trackers, isComparing);
        return renderMetricCell({ trackers: row.trackers, outreach: row.outreach, leads: row.leads, placements: row.placements_count, placementsCount: row.placements?.length || 0, conversion: row.conversion }, col.id, undefined, isComparing, () => handleOpenArticlesModal({ outreach_specialist: row.id, startDate, endDate }), isComparing && compareStartDate && compareEndDate ? () => handleOpenArticlesModal({ outreach_specialist: row.id, startDate: compareStartDate, endDate: compareEndDate }) : undefined, false, outreachTooltip, outreachPrevTooltip, trackerTooltips.tooltip, trackerTooltips.prevTooltip);
    }, [isComparing, handleOpenArticlesModal, startDate, endDate, compareStartDate, compareEndDate]);
    const renderClientCell = useCallback((row, col) => {
        if (col.id === "rowLabel") {
            const isInactive = row.status === "inactive";
            const isCurrent = Boolean(row.is_current ?? ((row.outreach?.actual_outreach ?? 0) > 0 ||
                (row.leads?.actual_lead ?? 0) > 0 ||
                ((row.placements_count?.actual_placement ?? row.placements?.length ?? 0) > 0) ||
                (row.outreach?.goal_outreach ?? 0) > 0 ||
                row.is_assigned_current ||
                row.is_assigned ||
                row.status === "active"));
            const isPrev = Boolean(row.is_prev ?? ((row.outreach?.prev_outreach ?? 0) > 0 ||
                (row.leads?.prev_lead ?? 0) > 0 ||
                ((row.placements_count?.prev_placement ?? 0) > 0) ||
                row.is_assigned_prev));
            const showCurrentBadge = !isComparing ? true : isCurrent;
            const showPrevBadge = isComparing && isPrev;
            return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: "2px", px: 0.1, py: 0.25, justifyContent: "center" }, children: [_jsx(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden" }, children: _jsx(HoverTooltip, { title: isInactive ? `${row.name} (Inactive)` : row.name, placement: "top", arrow: true, children: _jsxs(Typography, { className: "all-Font-Sizes", sx: {
                                    fontSize: "14px !important",
                                    fontWeight: 400,
                                    color: "#27272a",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "160px",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                }, children: [row.name, isInactive && (_jsx(Box, { component: "sup", sx: {
                                            color: "#ef4444",
                                            fontWeight: 700,
                                            ml: "2px",
                                            fontSize: "12px",
                                            lineHeight: 0,
                                        }, children: "*" }))] }) }) }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: "8px", mt: "2px" }, children: [showCurrentBadge && (_jsxs(Box, { sx: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                }, children: [_jsx(Box, { component: "span", sx: {
                                            width: "6.5px",
                                            height: "6.5px",
                                            borderRadius: "50%",
                                            backgroundColor: "#10B981",
                                            display: "inline-block",
                                            boxShadow: "0 0 0 2px #ECFDF5",
                                            flexShrink: 0,
                                        } }), _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                            fontSize: "11px !important",
                                            fontWeight: 500,
                                            color: "#6b7280",
                                            lineHeight: 1,
                                            userSelect: "none",
                                        }, children: "Current" })] })), showPrevBadge && (_jsxs(Box, { sx: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                }, children: [_jsx(Box, { component: "span", sx: {
                                            width: "6.5px",
                                            height: "6.5px",
                                            borderRadius: "50%",
                                            backgroundColor: "#F59E0B",
                                            display: "inline-block",
                                            boxShadow: "0 0 0 2px #FFF7ED",
                                            flexShrink: 0,
                                        } }), _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                            fontSize: "11px !important",
                                            fontWeight: 500,
                                            color: "#6b7280",
                                            lineHeight: 1,
                                            userSelect: "none",
                                        }, children: "Prev" })] }))] })] }));
        }
        const trackerTooltips = buildTrackerTooltips(row.trackers, isComparing);
        return renderMetricCell({ trackers: row.trackers, outreach: row.outreach, leads: row.leads, placements: row.placements_count, placementsCount: row.placements?.length || 0, conversion: row.conversion }, col.id, undefined, isComparing, () => handleOpenArticlesModal({ client_id: row.id, startDate, endDate }), isComparing && compareStartDate && compareEndDate ? () => handleOpenArticlesModal({ client_id: row.id, startDate: compareStartDate, endDate: compareEndDate }) : undefined, false, undefined, undefined, trackerTooltips.tooltip, trackerTooltips.prevTooltip);
    }, [isComparing, handleOpenArticlesModal, startDate, endDate, compareStartDate, compareEndDate]);
    const renderSpecialistSummaryRow = useCallback((_, col) => {
        if (!computedTeamTotal)
            return "";
        if (col.id === "rowLabel") {
            const specialistCount = computedTeamTotal.specialist_count ?? visibleEntities.length ?? 0;
            const assignedTotal = computedTeamTotal.assigned_client_count ?? summaryAssignedClients.length;
            const prevAssignedTotal = computedTeamTotal.prev_assigned_client_count ?? summaryPrevAssignedClients.length;
            const hasAssignedTooltip = assignedTotal > 0 && summaryAssignedClients.length > 0;
            const hasPrevAssignedTooltip = isComparing && prevAssignedTotal > 0 && summaryPrevAssignedClients.length > 0;
            const subtext = (_jsxs(Box, { sx: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "6px", rowGap: "1px", mt: "2px" }, children: [_jsxs(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", color: "#71717A", whiteSpace: "nowrap" }, children: ["Specialist (", specialistCount, ")"] }), hasAssignedTooltip ? (_jsx(HoverTooltip, { title: _jsx(ClientListTooltipContent, { title: isComparing ? "Current" : "Assigned", clients: summaryAssignedClients, countField: "goal_outreach" }), placement: "top-start", children: _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                fontSize: "12px !important",
                                borderBottom: "2px dotted #71717A",
                                color: "#71717A",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                "&:hover": { color: "#181d27" },
                            }, children: isComparing ? `Current (${assignedTotal})` : `Clients (${assignedTotal})` }) })) : (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", color: "#71717A", whiteSpace: "nowrap" }, children: isComparing ? `Current (${assignedTotal})` : `Clients (${assignedTotal})` })), isComparing && (hasPrevAssignedTooltip ? (_jsx(HoverTooltip, { title: _jsx(ClientListTooltipContent, { title: "Prev", clients: summaryPrevAssignedClients, countField: "goal_outreach" }), placement: "top-start", children: _jsxs(Typography, { className: "all-Font-Sizes", sx: {
                                fontSize: "12px !important",
                                borderBottom: "2px dotted #71717A",
                                color: "#71717A",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                "&:hover": { color: "#181d27" },
                            }, children: ["Prev (", prevAssignedTotal, ")"] }) })) : (_jsxs(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", color: "#71717A", whiteSpace: "nowrap" }, children: ["Prev (", prevAssignedTotal, ")"] })))] }));
            return (_jsxs(Box, { sx: { px: 0.1 }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", fontWeight: 700, color: "#181d27" }, children: "Total Summary" }), subtext] }));
        }
        const outreachTooltip = isComparing
            ? (summaryCurrentClients.length > 0 ? _jsx(ClientListTooltipContent, { title: "Current", clients: summaryCurrentClients, countField: "actual_outreach" }) : undefined)
            : (summaryAllClients.length > 0 ? _jsx(ClientListTooltipContent, { title: "Clients", clients: summaryAllClients, countField: "actual_outreach" }) : undefined);
        const outreachPrevTooltip = isComparing && summaryPrevClients.length > 0
            ? _jsx(ClientListTooltipContent, { title: "Prev", clients: summaryPrevClients, countField: "prev_outreach" })
            : undefined;
        const trackerTooltips = buildTrackerTooltips(computedTeamTotal.trackers, isComparing);
        return renderMetricCell({ trackers: computedTeamTotal.trackers, outreach: computedTeamTotal.outreach, leads: computedTeamTotal.leads, placements: computedTeamTotal.placements, placementsCount: computedTeamTotal.placements.total, conversion: computedTeamTotal.conversion }, col.id, undefined, isComparing, undefined, undefined, true, outreachTooltip, outreachPrevTooltip, trackerTooltips.tooltip, trackerTooltips.prevTooltip);
    }, [computedTeamTotal, isComparing, summaryAssignedClients, summaryPrevAssignedClients, summaryCurrentClients, summaryPrevClients, summaryAllClients]);
    const renderClientSummaryRow = useCallback((_, col) => {
        if (!computedTeamTotal)
            return "";
        if (col.id === "rowLabel") {
            const currentTotal = computedTeamTotal.client_count ?? visibleEntities.filter(e => e.is_current !== false).length;
            const prevTotal = computedTeamTotal.prev_client_count ?? visibleEntities.filter(e => Boolean(e.is_prev)).length;
            return (_jsxs(Box, { sx: { px: 0.1 }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: "14px !important", fontWeight: 700, color: "#181d27" }, children: "Total Summary" }), isComparing ? (_jsxs(Box, { sx: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "6px", rowGap: "1px", mt: "2px" }, children: [_jsxs(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", color: "#71717A", whiteSpace: "nowrap" }, children: ["Current (", currentTotal, ")"] }), _jsxs(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", color: "#71717A", whiteSpace: "nowrap" }, children: ["Prev (", prevTotal, ")"] })] })) : (_jsxs(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", color: "#71717A", mt: "2px" }, children: [currentTotal, " clients"] }))] }));
        }
        const trackerTooltips = buildTrackerTooltips(computedTeamTotal.trackers, isComparing);
        return renderMetricCell({ trackers: computedTeamTotal.trackers, outreach: computedTeamTotal.outreach, leads: computedTeamTotal.leads, placements: computedTeamTotal.placements, placementsCount: computedTeamTotal.placements.total, conversion: computedTeamTotal.conversion }, col.id, undefined, isComparing, undefined, undefined, true, undefined, undefined, trackerTooltips.tooltip, trackerTooltips.prevTooltip);
    }, [computedTeamTotal, isComparing, visibleEntities]);
    const isSpecialistView = groupBy === "specialist";
    const showSummaryRow = !!computedTeamTotal && visibleEntities.length > 0;
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: "8px" }, children: [_jsx(PagesCommonHeader, { lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastUpdated, onRefresh: handleSyncLeads, loading: syncingLeads, showIcon: canRefresh }), rightSection: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5 }, children: [_jsx(CustomDatePicker, { value: dateRangeValue, compareValue: compareRangeValue, onApply: handleApplyDateRange, comparison: true, disabled: loading }), _jsx(ExportButton, { onClick: handleExport })] }) }), _jsx(PagesCommonHeader, { leftSection: _jsxs(Typography, { className: "all-Font-Sizes", sx: { fontSize: "12px !important", fontWeight: 400, color: "#000" }, children: [_jsx(Box, { component: "span", sx: { color: "#ef4444", fontWeight: 700 }, children: "*" }), " Indicates an inactive client."] }), rightSection: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5 }, children: [canAddPlacement && (_jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", size: "small", onClick: handleAddPlacement, startIcon: _jsx(Plus, { size: 16 }), sx: { height: "36px", whiteSpace: "nowrap", fontSize: "13px", fontWeight: 500 }, children: "Add Placement" })), !isSpecialistView && (_jsx(Dropdown, { value: selectedSpecialist, onChange: (val) => setSelectedSpecialist(val), options: specialistOptions, placeholder: "Specialist: All", displayValue: _jsxs("span", { children: [_jsx("span", { style: { color: "#101828", fontWeight: 500 }, children: "Specialist:" }), " ", _jsx("span", { style: { color: "#667085" }, children: specialistOptions.find(o => o.value === selectedSpecialist)?.label?.replace("All Outreach Specialists", "All") || "All" })] }), leftIcon: "/assets/userC.svg", size: "sm", fullWidth: false, searchable: specialistOptions.length > 5, className: "header-dropdown", usePortal: true })), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { fontWeight: 500, fontSize: "13px !important", color: "#71717A" }, children: "By Client" }), _jsx(IOSSwitch, { checked: isSpecialistView, onChange: handleGroupByToggle }), _jsx(Typography, { className: "all-Font-Sizes", sx: { fontWeight: 500, fontSize: "13px !important", color: "#71717A" }, children: "By Outreach Specialist" })] })] }) }), _jsxs(Box, { className: "table-container", sx: { ...tableContainerSx, position: "relative" }, children: [_jsx(CommonTable, { columns: isSpecialistView ? SPECIALIST_COLUMNS : CLIENT_COLUMNS, rows: sortedRows, renderCell: isSpecialistView ? renderSpecialistCell : renderClientCell, isFirstLoad: loading && !entities.length, sortConfig: sortConfig, onSortChange: handleSortChange, expandTrigger: EXPAND_TRIGGERS.NONE, enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, enableColumnFilters: false, enableColumnVisibility: false, enableSelection: false, enableSummaryRow: showSummaryRow, renderSummaryRow: showSummaryRow ? (isSpecialistView ? renderSpecialistSummaryRow : renderClientSummaryRow) : undefined, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, type: TABLE_TYPES.GROUPED, isLoading: false, stickyHeader: true, maxHeight: "calc(100vh - 211px)", tableHeadClassName: "reusable-table-header-label all-Font-Sizes" }), loading && (_jsx(Box, { sx: { position: "absolute", inset: 0, background: "white", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }, children: _jsx(CommonLoader, { className: "table-loader" }) }))] })] }), _jsx(AddArticleModal, { open: addArticleOpen, onClose: () => setAddArticleOpen(false), onSaved: handleArticleSaved }), _jsx(AddPlacementDrawer, { open: addPlacementOpen, onClose: () => setAddPlacementOpen(false), onSaved: handleArticleSaved }), _jsx(PublishedArticlesModal, { open: publishedArticlesModalOpen, onClose: () => setPublishedArticlesModalOpen(false), filter: articlesFilter, onArticleChange: doFetch })] }));
}
